import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { RecipeCollection } from "../../models/recipe-collection";
import { makeId } from "../../utils/id-helper";
import { deleteRecipeAction, deleteCollectionAction } from "../actions";
import { makeSyncUpsert, makeSyncRemove } from "../syncUpsert";

export const recipeCollectionsAdapter = createEntityAdapter<RecipeCollection>();
type RecipeCollectionsState = EntityState<RecipeCollection, string>;
const initialState: RecipeCollectionsState = recipeCollectionsAdapter.getInitialState();

// Next free position at the end of a collection's manual order.
const nextSortOrder = (state: RecipeCollectionsState, collectionId: string): number =>
  Object.values(state.entities).reduce(
    (max, rc) =>
      rc && rc.collectionId === collectionId && !rc.deletedAt
        ? Math.max(max, rc.sortOrder ?? 0)
        : max,
    -1,
  ) + 1;

type RecipeCollectionPayload = {
  collectionId: string;
  recipeId: string;
};

type SetRecipeCollectionsPayload = {
  recipeId: string;
  collectionIds: string[];
};

type SetCollectionRecipesPayload = {
  collectionId: string;
  recipeIds: string[];
};

export const recipeCollectionsSlice = createSlice({
  name: "recipeCollections",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(recipeCollectionsAdapter),
    removeManyFromSync: makeSyncRemove(recipeCollectionsAdapter),
    addRecipeToCollection(
      state: RecipeCollectionsState,
      action: PayloadAction<RecipeCollectionPayload>,
    ): void {
      const { collectionId, recipeId } = action.payload;
      const now = new Date().toISOString();
      const id: string = makeId(collectionId, recipeId);

      const existing = state.entities[id];
      if (!existing) {
        // New link — append to the end of the collection's manual order.
        recipeCollectionsAdapter.addOne(state, {
          id,
          collectionId,
          recipeId,
          sortOrder: nextSortOrder(state, collectionId),
          createdAt: now,
          updatedAt: now,
        });
      } else if (existing.deletedAt) {
        // Resurrect a previously removed link — back to the end of the order.
        recipeCollectionsAdapter.updateOne(state, {
          id,
          changes: { deletedAt: undefined, sortOrder: nextSortOrder(state, collectionId) },
        });
      }
      // else: already a live link — no-op
    },
    // Soft-delete a single recipe↔collection link (e.g. swipe "Remove from
    // collection"). The recipe itself is untouched.
    removeRecipeFromCollection(
      state: RecipeCollectionsState,
      action: PayloadAction<RecipeCollectionPayload>,
    ): void {
      const { collectionId, recipeId } = action.payload;
      const id = makeId(collectionId, recipeId);
      const existing = state.entities[id];
      if (existing && !existing.deletedAt) {
        const now = new Date().toISOString();
        recipeCollectionsAdapter.updateOne(state, {
          id,
          changes: { deletedAt: now },
        });
      }
    },
    // Set the full recipe membership of one collection (the "Add recipes" sheet).
    // Mirror of setRecipeCollections but keyed by collection: soft-deletes links
    // for recipes no longer wanted, resurrects/inserts the rest.
    setCollectionRecipes(
      state: RecipeCollectionsState,
      action: PayloadAction<SetCollectionRecipesPayload>,
    ): void {
      const { collectionId, recipeIds } = action.payload;
      const now = new Date().toISOString();
      const desiredIds = new Set(
        recipeIds.map((recipeId) => makeId(collectionId, recipeId)),
      );

      const updates: { id: string; changes: Partial<RecipeCollection> }[] = [];

      // Soft-delete existing live links for this collection that are no longer wanted
      for (const rc of Object.values(state.entities)) {
        if (rc?.collectionId !== collectionId) continue;
        if (!desiredIds.has(rc.id) && !rc.deletedAt) {
          updates.push({ id: rc.id, changes: { deletedAt: now } });
        }
      }

      // Resurrect tombstoned rows we want back; insert genuinely new ones —
      // appended (in the given order) to the end of the collection's manual order.
      let sort = nextSortOrder(state, collectionId);
      const inserts: RecipeCollection[] = [];
      for (const recipeId of recipeIds) {
        const id = makeId(collectionId, recipeId);
        const existing = state.entities[id];
        if (!existing) {
          inserts.push({ id, collectionId, recipeId, sortOrder: sort++, createdAt: now, updatedAt: now });
        } else if (existing.deletedAt) {
          updates.push({ id, changes: { deletedAt: undefined, sortOrder: sort++ } });
        }
      }

      recipeCollectionsAdapter.updateMany(state, updates);
      if (inserts.length) recipeCollectionsAdapter.addMany(state, inserts);
    },
    // Write a new manual order for a collection: sortOrder = position in recipeIds.
    reorderCollectionRecipes(
      state: RecipeCollectionsState,
      action: PayloadAction<SetCollectionRecipesPayload>,
    ): void {
      const { collectionId, recipeIds } = action.payload;
      const updates: { id: string; changes: Partial<RecipeCollection> }[] = [];
      recipeIds.forEach((recipeId, index) => {
        const id = makeId(collectionId, recipeId);
        const existing = state.entities[id];
        if (existing && !existing.deletedAt && existing.sortOrder !== index) {
          updates.push({ id, changes: { sortOrder: index } });
        }
      });
      recipeCollectionsAdapter.updateMany(state, updates);
    },
    setRecipeCollections(
      state: RecipeCollectionsState,
      action: PayloadAction<SetRecipeCollectionsPayload>,
    ): void {
      const { recipeId, collectionIds } = action.payload;
      const now = new Date().toISOString();
      const desiredIds = new Set(
        collectionIds.map((collectionId) => makeId(collectionId, recipeId)),
      );

      const updates: { id: string; changes: Partial<RecipeCollection> }[] = [];

      // Soft-delete existing live rows for this recipe that are no longer wanted
      for (const rc of Object.values(state.entities)) {
        if (rc?.recipeId !== recipeId) continue;
        if (!desiredIds.has(rc.id) && !rc.deletedAt) {
          updates.push({ id: rc.id, changes: { deletedAt: now } });
        }
      }

      // Resurrect tombstoned rows we want back; insert genuinely new ones —
      // each appended to the end of that collection's manual order.
      const inserts: RecipeCollection[] = [];
      for (const collectionId of collectionIds) {
        const id = makeId(collectionId, recipeId);
        const existing = state.entities[id];
        if (!existing) {
          inserts.push({
            id,
            collectionId,
            recipeId,
            sortOrder: nextSortOrder(state, collectionId),
            createdAt: now,
            updatedAt: now,
          });
        } else if (existing.deletedAt) {
          updates.push({
            id,
            changes: { deletedAt: undefined, sortOrder: nextSortOrder(state, collectionId) },
          });
        }
      }

      recipeCollectionsAdapter.updateMany(state, updates);
      if (inserts.length) recipeCollectionsAdapter.addMany(state, inserts);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteRecipeAction, (state, action) => {
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter((rc) => rc?.recipeId === action.payload)
        .map((i) => ({ id: i!.id, changes: { deletedAt: now } }));
      recipeCollectionsAdapter.updateMany(state, updates);
    });
    builder.addCase(deleteCollectionAction, (state, action) => {
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter((rc) => rc?.collectionId === action.payload)
        .map((i) => ({ id: i!.id, changes: { deletedAt: now } }));
      recipeCollectionsAdapter.updateMany(state, updates);
    });
  },
});

const recipeCollectionSelectors = recipeCollectionsAdapter.getSelectors(
  (state: RootState) => state.recipeCollections,
);

// selectAll* hides soft-deleted links; sync reads entities directly.
export const selectAllRecipeCollections = createSelector(
  recipeCollectionSelectors.selectAll,
  (recipeCollections) => recipeCollections.filter((rc) => !rc.deletedAt),
);

// TODO look into more efficient ways to do this
export const selectRecipeCollectionByRecipeId = createSelector(
  [selectAllRecipeCollections, (_: RootState, recipeId: string) => recipeId],
  (recipeCollections, recipeId) =>
    recipeCollections.filter((rc) => rc.recipeId === recipeId),
);

export const selectRecipeCollectionsByCollectionId = createSelector(
  [selectAllRecipeCollections, (_: RootState, collectionId: string) => collectionId],
  (recipeCollections, collectionId) =>
    recipeCollections.filter((rc) => rc.collectionId === collectionId),
);

export const {
  addRecipeToCollection,
  removeRecipeFromCollection,
  setRecipeCollections,
  setCollectionRecipes,
  reorderCollectionRecipes,
  upsertManyFromSync: upsertRecipeCollectionsFromSync,
  removeManyFromSync: removeRecipeCollectionsFromSync,
} = recipeCollectionsSlice.actions;

export default recipeCollectionsSlice.reducer;
